import API from 'api/api';
import { normalizeListing, normalizeListingList } from '../utils/normalizeListing';
import { normalizeQuote, normalizeQuoteList } from '../utils/normalizeQuote';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const createListing = (data) =>
  API.post('/listings', data).then((r) => normalizeListing(r.data));

export const createListingDraft = (data) =>
  API.post('/listings/draft', data).then((r) => normalizeListing(r.data));

export const publishListingDraft = (id, documentIds = []) =>
  API.post(`/listings/${id}/publish`, { documentIds }).then((r) => normalizeListing(r.data));

export const getMyListings = (params) =>
  API.get('/listings/my', { params }).then((r) => {
    const payload = r.data;
    if (Array.isArray(payload)) return normalizeListingList(payload);
    if (Array.isArray(payload?.listings)) {
      return { ...payload, listings: normalizeListingList(payload.listings) };
    }
    if (Array.isArray(payload?.data)) {
      return { ...payload, data: normalizeListingList(payload.data) };
    }
    return payload;
  });

export const getMyListingsDashboard = () =>
  API.get('/listings/my/dashboard').then((r) => {
    const payload = r.data || {};
    return {
      ...payload,
      recentListings: normalizeListingList(payload.recentListings || []),
    };
  });

export const getPartnerDashboard = () =>
  API.get('/listings/partner/dashboard').then((r) => r.data);

export const getListingById = (id) =>
  API.get(`/listings/${id}`).then((r) => {
    const payload = r.data;
    if (payload?.listing || payload?.data) {
      return {
        ...payload,
        listing: normalizeListing(payload.listing ?? payload.data),
        myQuote: payload.myQuote ? normalizeQuote(payload.myQuote) : payload.myQuote,
      };
    }
    return normalizeListing(payload);
  });

export const updateListing = (id, data) =>
  API.put(`/listings/${id}`, data).then((r) => normalizeListing(r.data));

export const uploadListingImage = async (file, listingId) => {
  const fileType = file?.type || 'application/octet-stream';
  const requestResponse = await API.post('/documents/request-upload', {
    fileName: file.name,
    fileType,
    fileSize: file.size,
    entityType: 'listing',
    entityId: listingId,
    docType: 'listing_image',
  });

  const { documentId, uploadUrl, gcsPath } = requestResponse.data.data;
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': fileType,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Storage upload failed with status ${uploadResponse.status}`);
  }

  let lastError = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      await API.post(`/documents/${documentId}/confirm`);
      lastError = null;
      break;
    } catch (error) {
      lastError = error;
      if (attempt < 2) {
        await sleep(250 * (attempt + 1));
      }
    }
  }

  if (lastError) {
    throw lastError;
  }

  return {
    documentId,
    fileName: file.name,
    gcsPath,
  };
};

export const cancelListing = (id) =>
  API.delete(`/listings/${id}`).then((r) => r.data);

export const getListings = (params) =>
  API.get('/listings', { params }).then((r) => {
    const payload = r.data;
    if (Array.isArray(payload)) return normalizeListingList(payload);
    if (Array.isArray(payload?.listings)) {
      return { ...payload, listings: normalizeListingList(payload.listings) };
    }
    if (Array.isArray(payload?.data)) {
      return { ...payload, data: normalizeListingList(payload.data) };
    }
    return payload;
  });

export const getQuotesForListing = (listingId) =>
  API.get(`/listings/${listingId}/quotes`).then((r) => normalizeQuoteList(r.data));

export const submitQuote = (listingId, data) =>
  API.post(`/listings/${listingId}/quotes`, data).then((r) => normalizeQuote(r.data));

export const updateQuote = (listingId, quoteId, data) =>
  API.put(`/listings/${listingId}/quotes/${quoteId}`, data).then((r) => normalizeQuote(r.data));

export const acceptQuote = (listingId, quoteId) =>
  API.put(`/listings/${listingId}/quotes/${quoteId}/accept`).then((r) => r.data);

export const rejectQuote = (listingId, quoteId) =>
  API.put(`/listings/${listingId}/quotes/${quoteId}/reject`).then((r) => r.data);

export const withdrawQuote = (listingId, quoteId) =>
  API.delete(`/listings/${listingId}/quotes/${quoteId}`).then((r) => r.data);

export const acceptFixedPriceListing = (listingId) =>
  API.post(`/listings/${listingId}/accept-fixed-price`).then((r) => ({
    ...r.data,
    listing: r.data?.listing ? normalizeListing(r.data.listing) : r.data?.listing,
    quote: r.data?.quote ? normalizeQuote(r.data.quote) : r.data?.quote,
  }));

export const getMyQuotes = (params) =>
  API.get('/quotes/my', { params }).then((r) => normalizeQuoteList(r.data));

export const updateListingStatus = (id, status) =>
  API.put(`/listings/${id}/status`, { status }).then((r) => normalizeListing(r.data));

export const confirmPayment = (id) =>
  API.post(`/listings/${id}/confirm-payment`).then((r) => normalizeListing(r.data));

export const planListingTransport = (id, data) =>
  API.post(`/listings/${id}/plan`, data).then((r) => normalizeListing(r.data));

export const completeListing = (id) =>
  API.post(`/listings/${id}/complete`).then((r) => normalizeListing(r.data));

export const toggleWatch = (listingId) =>
  API.post(`/listings/${listingId}/watch`).then((r) => r.data);

export const downloadProformaInvoice = (listingId) =>
  API.get(`/listings/${listingId}/proforma-invoice`).then((r) => r.data);

export const downloadInvoice = (listingId) =>
  API.get(`/listings/${listingId}/invoice`).then((r) => r.data);

export const releaseListing = (listingId) =>
  API.post(`/listings/${listingId}/release`).then((r) => r.data);

export const refreshStripePaymentStatus = (id) =>
  API.post(`/listings/${id}/refresh-stripe-payment`).then((r) => ({
    ...r.data,
    listing: r.data?.listing ? normalizeListing(r.data.listing) : r.data?.listing,
  }));
